import React, { useState } from 'react';
import { Box, Popover, Checkbox, FormControlLabel, IconButton, Tooltip, Badge, Typography, Button, List, ListItem, ListItemIcon, ListItemSecondaryAction } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsIcon from '@mui/icons-material/Settings';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

export const TableToolbar = ({
  leftButtons,
  rightButtons,
  enableColumnVisibility,
  columnOrder = [],
  setColumnOrder,
  hiddenCols,
  setHiddenCols,
  pinnedCols,
  setPinnedCols,
  labels,
  activeFilterCount,
  isMobile,
  enableGlobalSearch,
  globalQuery,
  setGlobalQuery,
  numSelected = 0,
  selectionActions,
  selectionSummary,
  hiddenFields = [],
  colorMode = 'full',
  setColorMode
}) => {
  const [anchorCols, setAnchorCols] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [draggedKey, setDraggedKey] = useState(null);

  const toggleCol = (k) => {
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const togglePin = (k) => {
    setPinnedCols(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const handleDragStart = (e, key) => {
    setDraggedKey(key);
    e.dataTransfer.effectAllowed = 'move';
    // Transparent image to avoid default ghosting if needed, but standard is fine
  };

  const handleDragOver = (e, key) => {
    e.preventDefault();
    if (draggedKey === key) return;

    const newOrder = [...columnOrder];
    const draggedIdx = newOrder.indexOf(draggedKey);
    const hoverIdx = newOrder.indexOf(key);

    newOrder.splice(draggedIdx, 1);
    newOrder.splice(hoverIdx, 0, draggedKey);

    setColumnOrder(newOrder);
  };

  const handleDragEnd = () => {
    setDraggedKey(null);
  };

  const isSelectionMode = numSelected > 0;

  // Modo sin selección: franja ultracompacta solo con gear + rightButtons
  if (!isSelectionMode) {
    if (isMobile) return null;
    const hasRightContent = enableColumnVisibility || rightButtons;
    if (!hasRightContent) return null;
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        py: 0.6,
        gap: 0.5,
        borderBottom: '1px solid #cbd5e1',
        backgroundColor: '#f8fafc',
        minHeight: 34,
      }}>
        {/* Lado Izquierdo: Botones extras del padre (como los chips de Estatus) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, overflowX: 'auto', flex: 1, minWidth: 0 }}>
          {leftButtons}
        </Box>

        {/* Lado Derecho: Configuración y Excel */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {enableColumnVisibility && (
          <>
            <Tooltip title="Personalizar Vista y Columnas">
              <IconButton
                size="small"
                onClick={(e) => setAnchorCols(e.currentTarget)}
                sx={{
                  width: 22,
                  height: 22,
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  bgcolor: '#ffffff',
                  color: '#94a3b8',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: '#f0f4ff',
                    borderColor: '#0655A5',
                    color: '#0655A5',
                    transform: 'rotate(20deg)',
                  },
                }}
              >
                <SettingsIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Popover
              open={Boolean(anchorCols)}
              anchorEl={anchorCols}
              onClose={() => setAnchorCols(null)}
              disableRestoreFocus
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  mt: 0.75, p: 0, maxHeight: 520, width: 310,
                  borderRadius: '16px',
                  boxShadow: '0 20px 40px rgba(15,23,42,0.15)',
                  border: '1px solid #e2e8f0',
                }
              }}
            >
              {setColorMode && (
                <Box sx={{ p: 2, pb: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.78rem', mb: 1 }}>
                    🎨 Estilo de Diseño de Filas
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#ffffff', p: 0.4, borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                    {[
                      { key: 'full',      label: '🎨 Fila' },
                      { key: 'border',    label: '▍ Borde' },
                      { key: 'underline', label: '✨ Degradado' },
                    ].map(({ key, label }) => {
                      const active = colorMode === key;
                      return (
                        <Button key={key} size="small" fullWidth onClick={() => setColorMode(key)}
                          sx={{ height: 24, py: 0, fontSize: '0.68rem', fontWeight: active ? 700 : 600, textTransform: 'none', borderRadius: '6px',
                            color: active ? '#fff' : '#475569', bgcolor: active ? '#0655A5' : 'transparent', boxShadow: 'none',
                            '&:hover': { bgcolor: active ? '#054785' : '#f1f5f9' } }}
                        >{label}</Button>
                      );
                    })}
                  </Box>
                </Box>
              )}
              <Box sx={{ p: 2, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.78rem' }}>📋 Configuración de Columnas</Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.68rem' }}>Reordena arrastrando o fija con el pin</Typography>
              </Box>
              <List sx={{ py: 1 }}>
                {columnOrder.filter(k => !hiddenFields.includes(k)).map((k) => (
                  <ListItem key={k} draggable onDragStart={(e) => handleDragStart(e, k)} onDragOver={(e) => handleDragOver(e, k)} onDragEnd={handleDragEnd}
                    sx={{ py: 0.75, px: 2, mb: 0.25, transition: 'all 0.2s',
                      borderLeft: pinnedCols.has(k) ? '4px solid #0655A5' : '4px solid transparent',
                      backgroundColor: draggedKey === k ? '#f1f5f9' : (pinnedCols.has(k) ? '#eff6ff' : 'transparent'),
                      opacity: draggedKey === k ? 0.6 : 1, cursor: 'grab',
                      '&:hover': { backgroundColor: '#f1f5f9' }, '&:active': { cursor: 'grabbing' } }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}><DragIndicatorIcon fontSize="small" sx={{ color: '#cbd5e1' }} /></ListItemIcon>
                    <FormControlLabel
                      control={<Checkbox checked={!hiddenCols.has(k)} onChange={() => toggleCol(k)} size="small" sx={{ p: 0.5 }} />}
                      label={labels[k] || k}
                      sx={{ flex: 1, m: 0, '& .MuiTypography-root': { fontSize: 'clamp(0.62rem, 0.60rem + 0.12vw, 0.78rem)', fontWeight: pinnedCols.has(k) ? 700 : 500, color: pinnedCols.has(k) ? '#1e40af' : '#334155' } }}
                    />
                    <ListItemSecondaryAction sx={{ right: 8 }}>
                      <Tooltip title={pinnedCols.has(k) ? 'Desfijar' : 'Fijar'}>
                        <IconButton size="small" onClick={() => togglePin(k)} color={pinnedCols.has(k) ? 'primary' : 'default'}
                          sx={{ opacity: pinnedCols.has(k) ? 1 : 0.3, '&:hover': { opacity: 1, backgroundColor: 'rgba(0,0,0,0.04)' } }}
                        >
                          {pinnedCols.has(k) ? <PushPinIcon sx={{ fontSize: 18 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Popover>
          </>
        )}
        {rightButtons}
        </Box>
      </Box>
    );
  }

  // Modo selección: franja completa azul claro
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 0.5,
      py: 0.5,
      px: 1,
      backgroundColor: '#eff6ff',
      borderBottom: '1px solid #dbeafe',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      {/* ── SECCIÓN IZQUIERDA: Chips de Estatus (Scrollable en Móvil) ── */}
      <Box sx={{ 
        display: 'flex', 
        gap: 1, 
        alignItems: 'center', 
        flex: 1, 
        minWidth: 0, 
        overflowX: 'auto',
        pb: { xs: 0.5, md: 0 },
        '&::-webkit-scrollbar': { height: 2 },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 2 }
      }}>
        {isSelectionMode ? (
          <>
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e40af', whiteSpace: 'nowrap', textTransform: 'uppercase', fontSize: 'clamp(0.62rem, 0.60rem + 0.12vw, 0.78rem)', letterSpacing: '0.05em' }}>
                {numSelected} Seleccionado(s)
              </Typography>
              {selectionSummary && (
                <Box sx={{ borderLeft: '2px solid #dbeafe', pl: 2, display: { xs: 'none', sm: 'block' } }}>
                  {selectionSummary}
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {selectionActions}
            </Box>
          </>
        ) : (
          leftButtons ? (
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', flexWrap: 'nowrap', minWidth: 'max-content' }}>
              {leftButtons}
            </Box>
          ) : null
        )}
      </Box>

      {/* ── SECCIÓN DERECHA: Diseño, Columnas, Excel ── */}
      <Box sx={{ 
        display: 'flex', 
        gap: 0.8, 
        alignItems: 'center', 
        justify: { xs: 'space-between', sm: 'flex-end' }, 
        flexShrink: 0, 
        flexWrap: 'nowrap' 
      }}>
        
        {isMobile && !isSelectionMode && (
          <>
            <Tooltip title="Filtros y Búsqueda">
              <IconButton onClick={() => setMobileFiltersOpen(true)} sx={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Badge badgeContent={activeFilterCount} color="error">
                  <FilterListIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>
            <Popover
              open={mobileFiltersOpen}
              anchorReference="anchorPosition"
              anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
              transformOrigin={{ vertical: 'center', horizontal: 'center' }}
              onClose={() => setMobileFiltersOpen(false)}
              PaperProps={{ sx: { p: 1.5, width: '95vw', maxWidth: 400, maxHeight: '80vh', borderRadius: '16px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' } }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', textAlign: 'center', letterSpacing: '0.05em' }}>
                Filtros y Búsqueda
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {leftButtons}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button fullWidth variant="contained" onClick={() => setMobileFiltersOpen(false)} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                  Cerrar
                </Button>
              </Box>
            </Popover>
          </>
        )}

        {/* ── Toggle modo de color ── */}


        {enableColumnVisibility && (
          <>
            <Tooltip title="Personalizar Vista y Columnas (Estilo y Posición)">
              <IconButton 
                size="small"
                onClick={(e) => setAnchorCols(e.currentTarget)} 
                sx={{ 
                  width: 20,
                  height: 20,
                  border: '1px solid #cbd5e1',
                  borderRadius: '50%',
                  bgcolor: '#ffffff',
                  color: '#475569',
                  boxShadow: 'none',
                  p: 0,
                  '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8', color: '#0655A5' }
                }}
              >
                <SettingsIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Popover
              open={Boolean(anchorCols)}
              anchorEl={anchorCols}
              onClose={() => setAnchorCols(null)}
              disableRestoreFocus
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ 
                sx: { 
                  p: 0, 
                  maxHeight: 520, 
                  width: 310, 
                  borderRadius: '16px', 
                  boxShadow: '0 20px 40px rgba(15,23,42,0.15)',
                  border: '1px solid #e2e8f0',
                  mt: 1
                } 
              }}
            >
              {/* Bloque Superior: Selector de Estilo de Diseño */}
              {setColorMode && (
                <Box sx={{ p: 2, pb: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.78rem', mb: 1 }}>
                    🎨 Estilo de Diseño de Filas
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#ffffff', p: 0.4, borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                    {[
                      { key: 'full',   label: '🎨 Fila' },
                      { key: 'border', label: '▍ Borde' },
                      { key: 'underline', label: '✨ Degradado' },
                    ].map(({ key, label }) => {
                      const active = colorMode === key;
                      return (
                        <Button
                          key={key}
                          size="small"
                          fullWidth
                          onClick={() => setColorMode(key)}
                          sx={{
                            height: 24,
                            py: 0,
                            fontSize: '0.68rem',
                            fontWeight: active ? 700 : 600,
                            textTransform: 'none',
                            borderRadius: '6px',
                            color: active ? '#fff' : '#475569',
                            bgcolor: active ? '#0655A5' : 'transparent',
                            boxShadow: 'none',
                            '&:hover': {
                              bgcolor: active ? '#054785' : '#f1f5f9',
                            },
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Bloque Inferior: Reordenar y Fijar Columnas */}
              <Box sx={{ p: 2, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.78rem' }}>
                  📋 Configuración de Columnas
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.68rem' }}>
                  Reordena arrastrando o fija con el pin
                </Typography>
              </Box>
              <List sx={{ py: 1 }}>
                {columnOrder.filter(k => !hiddenFields.includes(k)).map((k) => (
                  <ListItem
                    key={k}
                    draggable
                    onDragStart={(e) => handleDragStart(e, k)}
                    onDragOver={(e) => handleDragOver(e, k)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      py: 0.75,
                      px: 2,
                      mb: 0.25,
                      transition: 'all 0.2s',
                      borderLeft: pinnedCols.has(k) ? '4px solid #0655A5' : '4px solid transparent',
                      backgroundColor: draggedKey === k ? '#f1f5f9' : (pinnedCols.has(k) ? '#eff6ff' : 'transparent'),
                      opacity: draggedKey === k ? 0.6 : 1,
                      cursor: 'grab',
                      '&:hover': { backgroundColor: '#f1f5f9' },
                      '&:active': { cursor: 'grabbing' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <DragIndicatorIcon fontSize="small" sx={{ color: '#cbd5e1' }} />
                    </ListItemIcon>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!hiddenCols.has(k)}
                          onChange={() => toggleCol(k)}
                          size="small"
                          sx={{ p: 0.5 }}
                        />
                      }
                      label={labels[k] || k}
                      sx={{ 
                        flex: 1, 
                        m: 0,
                        '& .MuiTypography-root': { 
                          fontSize: 'clamp(0.62rem, 0.60rem + 0.12vw, 0.78rem)', 
                          fontWeight: pinnedCols.has(k) ? 700 : 500,
                          color: pinnedCols.has(k) ? '#1e40af' : '#334155'
                        } 
                      }}
                    />
                    <ListItemSecondaryAction sx={{ right: 8 }}>
                      <Tooltip title={pinnedCols.has(k) ? 'Desfijar' : 'Fijar'}>
                        <IconButton 
                          size="small" 
                          onClick={() => togglePin(k)}
                          color={pinnedCols.has(k) ? 'primary' : 'default'}
                          sx={{ 
                            opacity: pinnedCols.has(k) ? 1 : 0.3,
                            '&:hover': { opacity: 1, backgroundColor: 'rgba(0,0,0,0.04)' }
                          }}
                        >
                          {pinnedCols.has(k) ? <PushPinIcon sx={{ fontSize: 18 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Popover>
          </>
        )}

        {rightButtons}
      </Box>
    </Box>
  );
};
